import qs from 'qs-stringify';
import { trimFilters } from '~/helpers/api-utils';

class _MissionsAPI {
  constructor() {
    this.includes = [
      'sla',
      'address',
      'createdBy',
      'offers',
      'offers.products',
      'offers.guidelines',
      'operator',
      'organization',
      'events',
      'events.reasons',
      'media',
      'states',
      'blocked_by',
      'orderItems.item',
    ];
    this.includesForId = [
      'media.files',
      'offers',
      'offers.guidelines',
      'offers.products.subtype',
      'states',
      'events',
      'events.createdBy',
      'events.blocker.createdBy',
      'events.reasons',
    ];
    this.includesForUploadList = [
      'events',
      'offers.products',
    ];
    this.includesForICNotReadyMap = [
      'address',
      'offers',
      'organization',
      'orderItems.item',
      'createdBy',
      'operator',
    ];

    this.includesAroundMe = [
      'address',
      'createdBy',
      'offers',
      'operator',
      'organization',
      'icBonusActive',
    ];

    this.includesMissionCart = [
      'address',
      'createdBy',
      'operator',
      'organization',
      'states',
      'blocked_by',
      'orderItems.item',
      'offers',
    ];
    this.versionPrefix = '/v2';
    this.url = `${this.versionPrefix}/missions/`;
    this.searchUrl = `${this.versionPrefix}/search/missions/`;
  }

  /**
   * Initialize.
   * TEMPORARY:
   *    The whole axios / auth should be replaced by proper install & usage.
   * @param {object} $axios - @nuxtjs/axios instance
   */
  init($axios) {
    this.API = $axios;
  }

  /**
   * Fetch Missions List,
   * applying filters to API request
   * @param filters
   * @param meta
   * @param sort
   * @returns {Promise<unknown>}
   */
  async fetchMissions(filters = {}, meta = {}, sort = 'created_at') {
    // Remove empty-string filters
    const nonEmptyFilters = trimFilters(filters);
    // Process meta
    const perPage = meta && meta.per_page ? meta.per_page : this.perPage;

    const requestURI = `${this.searchUrl}${'*'}`;

    try {
      const response = await this.API.get(requestURI, {
        params: {
          sort,
          per_page: perPage,
          include: this.includesForICNotReadyMap.join(','),
          ...nonEmptyFilters,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Missions List,
   * applying filters to API request
   * @return {array} that contains missions objects
   * @param queryParams
   * @param token
   */
  async fetchMissionList(queryParams, token) {
    const cancelToken = token ?? this.getCancelSource().token;
    const defaultParams = {
      include: this.includes.join(','),
    };

    const params = Object.assign(defaultParams, queryParams);

    const requestURI = `${this.searchUrl}*`;
    return this.API.get(requestURI, {
      cancelToken,
      params,
      paramsSerializer(p) {
        return qs(p);
      },
    });
  }

  async fetchMissionsAroundMe(filters = {}, meta = {}, sort = 'created_at') {
    // Remove empty-string filters
    const nonEmptyFilters = trimFilters(filters);
    // Process meta
    const perPage = meta.per_page;

    const requestURI = `${this.searchUrl}${'around'}`;

    try {
      const response = await this.API.get(requestURI, {
        params: {
          sort,
          per_page: perPage,
          include: this.includesAroundMe.join(','),
          ...nonEmptyFilters,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch a single Mission
   * @param {string} id - The mission ID
   */
  async fetchMission(id, noMedia) {
    try {
      let includes = this.includesForId;
      if (noMedia) {
        includes = includes.filter((include) => include !== 'media.files');
      }
      const response = await this.API.get(`${this.url}${id}?include=${includes}`);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Fetch a single Mission
   * @param {string} id - The mission ID
   */
  async fetchMissionCart(id) {
    try {
      const response = await this.API.get(`${this.url}${id}?include=${this.includesMissionCart.join(',')}`);
      return response.data;
    } catch {
      return null;
    }
  }

  getCancelSource() {
    return this.API.CancelToken.source();
  }

  /**
  * Fetch Missions List for upload pages
  * @param {object} queryParams - The queryParam Object ex: {tab: ongoing}
  * @return {array} that contains missions objects
  */
  async fetchUploadListMissions(queryParams) {
    const defaultParams = {
      include: this.includesForUploadList.join(','),
      media_stats: 'true',
    };

    const params = Object.assign(defaultParams, queryParams);

    const requestURI = `${this.url}`;
    try {
      const result = await this.API.get(requestURI, {
        params,
        paramsSerializer(p) {
          return qs(p);
        },
      });
      return result.data;
    } catch {
      return null;
    }
  }

  /**
   *
   * @param id
   * @returns {Promise<null|*>}
   */
  async fetchMissionMediaStats(id) {
    try {
      const response = await this.API.get(`${this.url}${id}`, {
        params: {
          media_stats: 'true',
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Post new Mission Event
   * @param {string} missionId - The mission ID
   * @param {object} attributes - Mission event attributes with 'code' and 'payload'
   * @return {object | any} true if success, { errors: ... } object if failure
   */
  async postMissionEvent(missionId, attributes) {
    const requestURI = `${this.url}${missionId}/events`;
    // Assemble request payload, according to expected API payload structure
    const payload = {
      data: {
        id: missionId,
        type: 'mission_event',
        attributes,
      },
    };
    // Launch request
    try {
      const response = await this.API.post(requestURI, payload);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Meta Groups of the mission
   * @param {string} missionId - The mission ID
   */
  async fetchMissionMetaGroups(missionId) {
    try {
      const response = await this.API.get(`${this.url}${missionId}/meta-groups`, {
        params: {
          per_page: 'all',
        },
      });
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * attach Meta to a media
   * @param {string} id - The mission ID
   * @param {string} metaId - The meta ID
   * @param {string} mediaId - The media ID
   */
  async attachMetaToMedia(missionId, mediaId, metaId) {
    const requestURI = `${this.url}${missionId}/media/${mediaId}/meta-groups/attach`;
    const payload = {
      data: {
        type: 'mission_meta_group',
        id: metaId,
      },
    };
    try {
      const response = await this.API.post(requestURI, payload);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * detach Meta from a media
   * @param {string} id - The mission ID
   * @param {string} metaId - The meta ID
   * @param {string} mediaId - The media ID
   */
  async detachMetaFromMedia(missionId, mediaId, metaId) {
    const requestURI = `${this.url}${missionId}/media/${mediaId}/meta-groups/detach`;
    const payload = {
      data: {
        type: 'mission_meta_group',
        id: metaId,
      },
    };
    try {
      const response = await this.API.delete(requestURI, payload);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Delete a custom Meta attached to a media
   * @param {string} id - The mission ID
   * @param {string} mediaId - The media ID
   * @param {string} metaId - The meta Id
   */
  async deleteCustomMeta(missionId, mediaId, metaId) {
    const requestURI = `${this.url}${missionId}/media/${mediaId}/metas/${metaId}`;
    try {
      const response = await this.API.delete(requestURI);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Create a custom Meta attached to a media
   * @param {string} id - The mission ID
   * @param {string} mediaId - The media ID
   * @param {{read_only: boolean, origin: string, value: (*), key: string}} attributes - The meta attributes
   */
  async createMetaForMedia(missionId, mediaId, attributes) {
    const requestURI = `${this.url}${missionId}/media/${mediaId}/metas`;
    const payload = {
      data: {
        type: 'mission_media_meta',
        attributes,
      },
    };
    try {
      const response = await this.API.post(requestURI, payload);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Update a custom Meta attached to a media
   * @param {string} id - The mission ID
   * @param {string} mediaId - The media ID
   * @param {string} metaId - The meta ID
   * @param {string} attributes - The meta attributes
   */
  async updateMetaForMedia(missionId, mediaId, metaId, attributes) {
    const requestURI = `${this.url}${missionId}/media/${mediaId}/metas/${metaId}`;
    const payload = {
      data: {
        id: metaId,
        type: 'mission_media_meta',
        attributes,
      },
    };
    try {
      const response = await this.API.patch(requestURI, payload);
      return response.data;
    } catch {
      return null;
    }
  }

  async getZipUploadUrl(missionId, chunkCount) {
    const requestURI = `${this.url}${missionId}/media/zip-upload`;
    const payload = {
      data: {
        type: 'mission_media_file_upload',
        attributes: {
          quantity: chunkCount,
        },
      },
    };
    try {
      const { data } = await this.API.post(requestURI, payload);
      return {
        urls: data?.data?.attributes?.urls,
        uploadId: data?.data?.id,
      };
    } catch (error) {
      // Format error response
      return { errors: error.response ? error.response.data : error };
    }
  }

  /**
  * Fetch Missions List for IC not ready map
  * @return {array} that contains missions objects
  */
   async fetchICNotReadyMissions() {
    const defaultParams = {
      include: this.includesForICNotReadyMap.join(','),
      'filter[status]': 'WAITING_ATTRIBUTION',
      'filter[without_blocker]': 1,
      'filter[without_operator]': 1,
      per_page: 'all',
    };

    const params = Object.assign(defaultParams);

    const requestURI = `${this.searchUrl}around`;
    try {
      const result = await this.API.get(requestURI, {
        params,
      });
      return result.data;
    } catch {
      return null;
    }
  }

  async fetchMapMissions(filters) {
    const perPage = 'all';
    const nonEmptyFilters = trimFilters(filters);
    try {
      const response = await this.API.get(`${this.versionPrefix}/search/locations/missions/current_status:WAITING_ATTRIBUTION`, {
        params: {
          per_page: perPage,
          ...nonEmptyFilters,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

const MissionsAPI = new _MissionsAPI();

export default MissionsAPI;
